import { push } from 'connected-react-router';
import isEmpty from 'lodash/isEmpty';
import { DFDHeadersObject, Nullable } from 'modules/types/common';
import { Alert, AlertButton, AlertOptions } from 'react-native';
import Config from 'react-native-config';
import { Dispatch } from 'redux';
import { getDownloadHeadersWithCurrentAccountConsumerId } from 'lib/downloads/utils';
import { getErrorMessage } from 'services/api/utils';
import FileServices from 'services/FileServices';
import { currentAccountConsumerFullNameSelector } from 'store/account/selectors';
import { fhirCurrentUserPatientId } from 'store/authentication/selectors';
import { MessagePool, ProviderSummary, AddComposeDocumentPayload } from 'store/findProvider/types';
import { currentConsumerPcpDoctorsSelector } from 'store/myDoctors/selectors';
import { Thunk } from 'store/types';
import { oc } from 'ts-optchain';
import {
  inboxRequestStateSelector,
  messageDetailDataSelector,
  messageDownloadsSelector,
  outboxRequestStateSelector
} from './selectors';
import {
  MessageAttachment,
  MessageComposeScreenNavigationParams,
  CacheControlHeaders
} from './types';
import { getMessageIsDownloading } from './utils';

export const GET_INBOX_CACHE_SUCCESS = 'MESSAGING.GET_INBOX_CACHE_SUCCESS';
export const GET_OUTBOX_CACHE_SUCCESS = 'MESSAGING.GET_OUTBOX_CACHE_SUCCESS';

export const GET_INBOX_MESSAGES = 'MESSAGING.GET_INBOX_MESSAGES';
export const GET_INBOX_MESSAGES_SUCCESS = 'MESSAGING.GET_INBOX_MESSAGES_SUCCESS';
export const GET_INBOX_MESSAGES_FAIL = 'MESSAGING.GET_INBOX_MESSAGES_FAIL';

export const getInboxMessages = (useRequestState = true) => ({
  type: GET_INBOX_MESSAGES,
  payload: {
    useRequestState,
    requestStateSelector: inboxRequestStateSelector,
    client: 'messaging',
    request: {
      method: 'get',
      url: '/messages',
      params: {
        messageFolder: 'inbox'
      },
      headers: { 'cache-control': 'no-cache' }
    }
  }
});

export const GET_OUTBOX_MESSAGES = 'MESSAGING.GET_OUTBOX_MESSAGES';
export const GET_OUTBOX_MESSAGES_SUCCESS = 'MESSAGING.GET_OUTBOX_MESSAGES_SUCCESS';
export const GET_OUTBOX_MESSAGES_FAIL = 'MESSAGING.GET_OUTBOX_MESSAGES_FAIL';
export const getOutboxMessages = (useRequestState = true) => {
  const headers: CacheControlHeaders = {};
  if (!useRequestState && Config.MESSAGING_EXPANSION_BASE_URL) {
    headers['Cache-Control'] = 'no-cache';
  }

  return {
    type: GET_OUTBOX_MESSAGES,
    payload: {
      useRequestState,
      requestStateSelector: outboxRequestStateSelector,
      client: 'messaging',
      request: {
        method: 'get',
        url: '/messages',
        params: {
          messageFolder: 'outbox'
        },
        headers
      }
    }
  };
};

export const getAllMessages = (): Thunk => dispatch =>
  Promise.all([dispatch(getInboxMessages()), dispatch(getOutboxMessages())]);

export const APPLY_INBOX_SEARCH = 'MESSAGING.APPLY_INBOX_SEARCH_FILTERS';
export const applyInboxSearch = (searchText: string) => ({
  type: APPLY_INBOX_SEARCH,
  payload: searchText
});

export const CLEAR_INBOX_SEARCH = 'MESSAGING.CLEAR_INBOX_SEARCH';
export const clearInboxSearch = () => ({
  type: CLEAR_INBOX_SEARCH
});

export const APPLY_OUTBOX_SEARCH = 'MESSAGING.APPLY_OUTBOX_SEARCH_FILTERS';
export const applyOutboxSearch = (searchText: string) => ({
  type: APPLY_OUTBOX_SEARCH,
  payload: searchText
});

export const CLEAR_OUTBOX_SEARCH = 'MESSAGING.CLEAR_OUTBOX_SEARCH';
export const clearOutboxSearch = () => ({
  type: CLEAR_OUTBOX_SEARCH
});

export const loadAllMessages = (): Thunk => (dispatch: Dispatch) => {
  return Promise.all([dispatch(getInboxMessages()), dispatch(getOutboxMessages())]);
};

export const DOWNLOAD_MESSAGE_ATTACHMENT = 'MESSAGING.DOWNLOAD_MESSAGE_ATTACHMENT';
export const DOWNLOAD_MESSAGE_ATTACHMENT_SUCCESS = 'MESSAGING.DOWNLOAD_MESSAGE_ATTACHMENT_SUCCESS';
export const DOWNLOAD_MESSAGE_ATTACHMENT_FAIL = 'MESSAGING.DOWNLOAD_MESSAGE_ATTACHMENT_FAIL';
export const downloadMessageAttachment = (attachment: MessageAttachment): Thunk => async (
  dispatch,
  getState
) => {
  const state = getState();

  if (getMessageIsDownloading(messageDownloadsSelector(state), attachment)) {
    return;
  }

  dispatch({ type: DOWNLOAD_MESSAGE_ATTACHMENT, payload: { attachment } });

  try {
    let headers: DFDHeadersObject = {};
    let url:
      | string
      | undefined = `${Config.MESSAGING_BASE_URL}/attachment/${attachment.mediaIdentifier}?fileName=${attachment.title}`;

    if (Config.MESSAGING_EXPANSION_BASE_URL) {
      url = attachment.mhpDetails?.url;
      if (!url) {
        throw Error('Attachment download not available');
      }
      if (!isEmpty(attachment.mhpDetails?.headers)) {
        headers = {
          ...attachment.mhpDetails?.headers
        };
      }
    }

    headers = {
      ...headers,
      ...getDownloadHeadersWithCurrentAccountConsumerId(state, url)
    };
    const response = await FileServices.download(
      `${attachment.title.split('/').join('-')}`,
      url,
      headers,
      attachment.format
    );

    dispatch({ type: DOWNLOAD_MESSAGE_ATTACHMENT_SUCCESS, payload: { response, attachment } });
  } catch (error) {
    dispatch({ type: DOWNLOAD_MESSAGE_ATTACHMENT_FAIL, payload: { attachment }, error });
  }
};

export const MESSAGE_COMPOSE_SEARCH_UPDATED = 'MESSAGE.MESSAGE_COMPOSE_SEARCH_UPDATED';
export const messageSearchUpdated = (search: string) => ({
  type: MESSAGE_COMPOSE_SEARCH_UPDATED,
  payload: search
});

export const MESSAGE_COMPOSE_POOL_SELECTED = 'MESSAGE.MESSAGE_COMPOSE_POOL_SELECTED';
export const messageComposePoolSelected = (pool: MessagePool) => ({
  type: MESSAGE_COMPOSE_POOL_SELECTED,
  payload: pool
});

export const messageReplyProviderSelected = (provider: ProviderSummary): Thunk => dispatch => {
  if (!provider.messagingPools.length) {
    Alert.alert(
      'The Provider on file could not be messaged at this time',
      'Please select another provider'
    );
    return dispatch({ type: 'MESSAGE.PROVIDER_SELECTED_FAIL', payload: provider });
  }

  if (provider.messagingPools.length === 1) {
    dispatch(messageComposePoolSelected(provider.messagingPools[0]));
    return dispatch(push('/u/messages'));
  }

  return dispatch(push('/u/messages/pool', { provider }));
};

export const messageComposeProviderSelected = (
  provider: ProviderSummary,
  params
): Thunk => dispatch => {
  if (!provider.messagingPools.length) {
    Alert.alert('Sorry', 'This provider is not messageable');
    return dispatch({ type: 'MESSAGE.PROVIDER_SELECTED_FAIL', payload: provider });
  }

  if (provider.messagingPools.length === 1) {
    dispatch(messageComposePoolSelected(provider.messagingPools[0]));
    return dispatch(push('/u/messages', { ...params }));
  }

  return dispatch(push('/u/messages/pool', { provider, ...params }));
};

export const MESSAGE_COMPOSE_CANCEL = 'MESSAGE.MESSAGE_COMPOSE_CANCEL';
export const cancelComposeMessage = () => ({ type: MESSAGE_COMPOSE_CANCEL });

export const MESSAGE_COMPOSE_SEND = 'MESSAGE.MESSAGE_COMPOSE_SEND';
export const MESSAGE_COMPOSE_SEND_SUCCESS = 'MESSAGE.MESSAGE_COMPOSE_SEND_SUCCESS';
export const MESSAGE_COMPOSE_SEND_FAIL = 'MESSAGE.MESSAGE_COMPOSE_SEND_FAIL';

export const sendComposeMessage = (
  pool: MessagePool,
  subject: string,
  text: string,
  messageUUID: string,
  attachmentUUIDArray: string[]
): Thunk => async (dispatch, getState) => {
  if (!pool || !subject || !text) {
    return { type: MESSAGE_COMPOSE_SEND_FAIL, error: 'Incorrect data supplied' };
  }

  const result = await dispatch({
    type: MESSAGE_COMPOSE_SEND,
    payload: {
      client: 'messaging',
      request: {
        method: 'post',
        url: '/messages',
        data: {
          patientId: fhirCurrentUserPatientId(getState()),
          displayName: currentAccountConsumerFullNameSelector(getState()),
          previousMessageId: messageDetailDataSelector(getState())?.id,
          text,
          receivers: [pool],
          subject,
          messageUUID,
          attachmentUUIDArray
        }
      }
    }
  });
  if (result.payload) {
    dispatch(getOutboxMessages(false));
  }
  return result;
};

export const ADD_COMPOSE_DOCUMENT = `MESSAGE.ADD_COMPOSE_DOCUMENT`;
export const ADD_COMPOSE_DOCUMENT_SUCCESS = `MESSAGE.ADD_COMPOSE_DOCUMENT_SUCCESS`;
export const ADD_COMPOSE_DOCUMENT_FAIL = `MESSAGE.ADD_COMPOSE_DOCUMENT_FAIL`;

export const addComposeDocument = (payload: AddComposeDocumentPayload): Thunk => async dispatch => {
  // eslint-disable-next-line no-undef
  const formData = new FormData();
  formData.append('file', payload.file);
  formData.append('messageUUID', payload.messageUUID);

  let url = '/attachment';
  if (Config.MESSAGING_EXPANSION_BASE_URL) {
    url = '/adapters/utah/attachments/_upload';
  }

  const result = await dispatch({
    type: ADD_COMPOSE_DOCUMENT,
    payload: {
      client: 'messaging',
      request: {
        method: 'post',
        headers: { 'Content-Type': 'multipart/form-data' },
        url,
        data: formData
      }
    }
  });

  if (result.error) {
    return {
      error: getErrorMessage(result.error)
    };
  }
  return {
    attachmentId: result.payload?.data?.attachmentId
  };
};

export const MESSAGE_PROVIDER_SEARCH = 'MESSAGE.MESSAGE_PROVIDER_SEARCH';
export const MESSAGE_PROVIDER_SEARCH_SUCCESS = 'MESSAGE.MESSAGE_PROVIDER_SEARCH_SUCCESS';
export const MESSAGE_PROVIDER_SEARCH_FAIL = 'MESSAGE.MESSAGE_PROVIDER_SEARCH_FAIL';
export const searchMessagableProviders = (value = ''): Thunk => dispatch => {
  if (!value) {
    return;
  }

  dispatch({
    type: MESSAGE_PROVIDER_SEARCH,
    payload: {
      client: 'provider',
      request: {
        method: 'get',
        url: '/provider/messagingPoolSearch',
        params: {
          searchText: value.trim()
        }
      }
    }
  });
};

export const getProviderRecepientForReply = (
  providerId: string
): Thunk<Promise<Nullable<ProviderSummary>>> => async dispatch => {
  if (!providerId) {
    return null;
  }

  const result = await dispatch({
    type: 'MESSAGE.GET_PROVIDER_FOR_REPLY',
    payload: {
      client: 'provider',
      request: {
        url: `${Config.PROVIDER_URL_PROVIDER_DETAILS}/${providerId}`,
        params: {
          idType: 'cernerPersonnelId'
        }
      }
    }
  });

  if (result.error || !result.payload.data) {
    return null;
  }

  const { code, messagingPools } = result.payload.data;
  if (code === 'TYPE_ERROR' || messagingPools.length === 0) return null;

  return result.payload.data;
};

export const startDirectReply = (
  messageParams: MessageComposeScreenNavigationParams,
  messagePool: MessagePool,
  senderPoolName: string
): Thunk => dispatch => {
  if (!messagePool.groupDisplayName) {
    messagePool = { ...messagePool, groupDisplayName: senderPoolName || 'Unknown Sender' };
  }

  dispatch(messageComposePoolSelected(messagePool));
  dispatch(push('/u/messages', { ...messageParams }));
};

export const messageTransmit = (
  messageParams: MessageComposeScreenNavigationParams
) => dispatch => {
  dispatch(push('/u/messages', { ...messageParams }));
};

export const messageShare = (messageParams: MessageComposeScreenNavigationParams) => {
  return push('/u/messages', messageParams);
};

export const messageReply = (
  messageParams: MessageComposeScreenNavigationParams,
  cernerPersonelId: string,
  cancelOnNoProviderFound = false
): Thunk => async dispatch => {
  const provider: Nullable<ProviderSummary> = await dispatch(
    getProviderRecepientForReply(cernerPersonelId)
  );

  if (cancelOnNoProviderFound && !oc(provider).messagingPools.length()) {
    Alert.alert('The Provider on file could not be messaged at this time');
    return;
  }

  dispatch(push('/u/messages', { ...messageParams }));

  if (provider) {
    dispatch(messageReplyProviderSelected(provider));
  } else {
    Alert.alert(
      'The Provider on file could not be messaged at this time',
      'Please select another provider'
    );
  }
};

interface UnavailableParams {
  cancel: boolean;
  alert: {
    title: string;
    message?: string;
    buttons?: AlertButton[];
    options?: AlertOptions;
  };
}

const defaultUnavailableParams = {
  cancel: false,
  alert: {
    title: '',
    message:
      'Your prescribing physician and/or primary care provider could not be messaged at this time'
  }
};

export const messageWithProviderOrPcp = (
  provider: ProviderSummary | null,
  messageParams: MessageComposeScreenNavigationParams,
  unavailableParams: UnavailableParams = defaultUnavailableParams
): Thunk => (dispatch, getState) => {
  // Provider id and availability
  const cernerPersonnelId = oc(provider).cernerPersonnelId('');
  const messagingPools = oc(provider).messagingPools([]);

  // Provider Unavaiable
  if (!messagingPools.length) {
    const consumerPcpList = currentConsumerPcpDoctorsSelector(getState());
    const isPcp = consumerPcpList.some(pcp => pcp.cernerPersonnelId === cernerPersonnelId);

    // Provider is not PCP and Consumer has PCP
    if (!isPcp && consumerPcpList.length) {
      const consumerPcp = consumerPcpList[0];
      Alert.alert(
        'Your prescribing physician could not be messaged at this time',
        'Would you like to message your primary care provider?',
        [
          { text: 'NO', onPress: () => {} },
          {
            text: 'YES',
            onPress: () =>
              dispatch(
                messageWithProviderOrPcp(consumerPcp, messageParams, {
                  ...unavailableParams,
                  cancel: true
                })
              )
          }
        ]
      );
    } else {
      // @ts-ignore
      Alert.alert(...Object.values(unavailableParams.alert));
    }

    if (unavailableParams.cancel) return;
    dispatch(push('/u/messages', { ...messageParams }));
    return;
  }

  // Provider has one availablility
  if (messagingPools.length === 1) {
    dispatch(messageComposePoolSelected(provider.messagingPools[0]));
    dispatch(push('/u/messages', { ...messageParams }));
    return;
  }

  // Provider has multiple availabilities
  dispatch(push('/u/messages/pool', { provider, messageParams }));
};

export const messageQuestion = (
  messageParams: MessageComposeScreenNavigationParams,
  cernerPersonnelId?: string
): Thunk => async dispatch => {
  const unavailableParams = {
    cancel: false,
    alert: {
      title: 'Your ordering physician and PCP cannot be messaged',
      message: 'Please search for a provider.'
    }
  };

  if (messageParams.recipient) {
    dispatch(messageWithProviderOrPcp(messageParams.recipient, messageParams, unavailableParams));
  } else if (cernerPersonnelId) {
    const provider: ProviderSummary | null = await dispatch(
      getProviderRecepientForReply(cernerPersonnelId)
    );
    dispatch(messageWithProviderOrPcp(provider, messageParams, unavailableParams));
  } else {
    dispatch(push('/u/messages', { ...messageParams }));
  }
};

export const messageRenewal = (
  messageParams: MessageComposeScreenNavigationParams,
  cernerPersonnelId: string
): Thunk => async dispatch => {
  const unavailableParams = {
    cancel: true,
    alert: {
      title: 'Your ordering physician and PCP cannot be messaged',
      message: 'Please call to renew.'
    }
  };

  const provider: ProviderSummary | null = await dispatch(
    getProviderRecepientForReply(cernerPersonnelId)
  );

  dispatch(messageWithProviderOrPcp(provider, messageParams, unavailableParams));
};

export * from './actions.common';
